import { ChangeEvent, useEffect, useMemo, useRef, useState } from "react";
import { Button, Table, Form, Dropdown, Offcanvas, Badge } from "react-bootstrap";
import ReactPaginate from "react-paginate";
import { toast } from "react-toastify";
import { PropagateLoader } from "react-spinners";
import { useSearchParams } from "react-router-dom";
import {
  FaBox,
  FaPlus,
  FaEdit,
  FaTrash,
  FaDownload,
  FaImage,
  FaChevronLeft,
  FaChevronRight,
  FaTimes,
  FaUpload,
} from "react-icons/fa";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { useTranslate } from "../hooks/LanguageProvider";

const backendUrl = process.env.REACT_APP_BACKEND_URL || "http://localhost:5002";

type ProductImage = string;

interface Product {
  id_product: number;
  product_name: string;
  category: string;
  material: string;
  annual_capacity: number;
  unit: string;
  constructor_name?: string;
  requested_capacity?: number | null;
  currently_imported?: number;
  import_substitution?: number;
  homologation_status?: string;
  export_status?: string;
  product_images?: ProductImage[] | string;
}

interface NewImage {
  file: File;
  preview: string;
}

const emptyForm = {
  product_name: "",
  category: "",
  material: "",
  annual_capacity: "",
  unit: "pcs/year",
  constructor_name: "",
  requested_capacity: "",
  currently_imported: "0",
  import_substitution: "0",
  homologation_status: "",
  export_status: "",
};

const normalizeImages = (value: Product["product_images"]): string[] => {
  if (!value) return [];

  if (Array.isArray(value)) {
    return value.map((item) => String(item).trim()).filter(Boolean);
  }

  if (typeof value === "string") {
    const cleanValue = value.trim();
    if (!cleanValue) return [];

    try {
      const parsed = JSON.parse(cleanValue);
      if (Array.isArray(parsed)) {
        return parsed.map((item) => String(item).trim()).filter(Boolean);
      }
    } catch {
      return cleanValue
        .split("|||")
        .map((item) => item.trim())
        .filter(Boolean);
    }
  }

  return [];
};

const getImageUrl = (path: string) => {
  if (!path) return "";

  const value = String(path).trim();

  if (!value) return "";

  if (/^https?:\/\//i.test(value)) {
    return value;
  }

  if (value.startsWith("blob:")) {
    return value;
  }

  if (value.startsWith("data:image")) {
    return value;
  }

  if (value.startsWith("file:///")) {
    const cleanPath = value.replace("file://", "");

    const publicUploadsIndex = cleanPath.indexOf("/public/uploads/");
    if (publicUploadsIndex !== -1) {
      const relativePath = cleanPath.substring(publicUploadsIndex + "/public".length);
      return `${backendUrl}${relativePath}`;
    }

    const uploadsIndex = cleanPath.indexOf("/uploads/");
    if (uploadsIndex !== -1) {
      const relativePath = cleanPath.substring(uploadsIndex);
      return `${backendUrl}${relativePath}`;
    }
  }

  if (value.includes("/API/public/uploads/")) {
    const cleanPath = value.split("/API/public/uploads/")[1];
    return `${backendUrl}/uploads/${cleanPath}`;
  }

  if (value.includes("API/public/uploads/")) {
    const cleanPath = value.split("API/public/uploads/")[1];
    return `${backendUrl}/uploads/${cleanPath}`;
  }

  if (value.includes("/public/uploads/")) {
    const cleanPath = value.split("/public/uploads/")[1];
    return `${backendUrl}/uploads/${cleanPath}`;
  }

  if (value.startsWith("/uploads/")) {
    return `${backendUrl}${value}`;
  }

  if (value.startsWith("uploads/")) {
    return `${backendUrl}/${value}`;
  }

  if (value.startsWith("/supplier-products/")) {
    return `${backendUrl}/uploads${value}`;
  }

  if (value.startsWith("supplier-products/")) {
    return `${backendUrl}/uploads/${value}`;
  }

  if (value.startsWith("/")) {
    return `${backendUrl}${value}`;
  }

  return `${backendUrl}/uploads/supplier-products/${value}`;
};

const displayValue = (value: unknown) => {
  if (value === null || value === undefined || value === "") return "Non renseigné";
  return String(value);
};

const formatYesNo = (value: unknown) => {
  if (
    value === 1 ||
    value === "1" ||
    value === true ||
    value === "true" ||
    value === "Oui" ||
    value === "oui"
  ) {
    return "Oui";
  }

  return "Non";
};

const getCapacityGap = (annual?: number, requested?: number | null) => {
  const a = Number(annual || 0);
  const r = Number(requested || 0);

  if (!r) return null;

  return a - r;
};

const getCapacityStatus = (annual?: number, requested?: number | null) => {
  const gap = getCapacityGap(annual, requested);

  if (gap === null) return "Non renseigné";

  return gap >= 0 ? "Capacitaire" : "Non capacitaire";
};

const getCapacityBadgeClass = (status: string) => {
  if (status === "Capacitaire") return "badge bg-success";
  if (status === "Non capacitaire") return "badge bg-danger";
  return "badge bg-secondary";
};

export default function SupplierProducts() {
  const { translate } = useTranslate();
  const t = translate;

  const [searchParams] = useSearchParams();

  const id_user =
    searchParams.get("id_user") ??
    localStorage.getItem("userid") ??
    localStorage.getItem("id_user") ??
    localStorage.getItem("GeopUserID") ??
    "";

  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [search, setSearch] = useState("");
  const [searchField, setSearchField] = useState("product_name");

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);

  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(10);

  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());

  const [form, setForm] = useState(emptyForm);
  const [existingImages, setExistingImages] = useState<string[]>([]);
  const [newImages, setNewImages] = useState<NewImage[]>([]);
  const [activeImageIndex, setActiveImageIndex] = useState(0);

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  async function loadProducts() {
    if (!id_user) return;

    setLoading(true);

    try {
      const res = await fetch(`${backendUrl}/api/supplier-products/${id_user}`);
      const data = await res.json();

      setProducts(Array.isArray(data) ? data : []);
    } catch (error) {
      toast.error("Erreur pendant le chargement des produits");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id_user]);

  useEffect(() => {
    return () => {
      newImages.forEach((img) => URL.revokeObjectURL(img.preview));
    };
  }, [newImages]);

  const allDrawerImages = useMemo(() => {
    const oldImages = existingImages.map((path) => ({
      type: "old",
      path,
      preview: getImageUrl(path),
    }));

    const uploadedImages = newImages.map((img) => ({
      type: "new",
      path: img.file.name,
      preview: img.preview,
    }));

    return [...oldImages, ...uploadedImages];
  }, [existingImages, newImages]);

  const filtered = useMemo(() => {
    const text = search.trim().toLowerCase();

    if (!text) return products;

    return products.filter((p) => {
      const value = String((p as any)[searchField] ?? "").toLowerCase();
      return value.includes(text);
    });
  }, [products, search, searchField]);

  const pageCount = Math.ceil(filtered.length / limit) || 1;
  const current = filtered.slice(page * limit, page * limit + limit);

  const currentIds = current.map((p) => p.id_product);

  const allCurrentSelected =
    currentIds.length > 0 && currentIds.every((id) => selectedIds.has(id));

  const productsForExport =
    selectedIds.size > 0
      ? filtered.filter((p) => selectedIds.has(p.id_product))
      : filtered;

  const selectedData = productsForExport.map((p) => ({
    product_name: p.product_name,
    category: p.category,
    material: p.material,
    annual_capacity: p.annual_capacity,
    unit: p.unit,
    constructor_name: p.constructor_name,
    requested_capacity: p.requested_capacity,
    capacity_gap: getCapacityGap(p.annual_capacity, p.requested_capacity),
    capacity_status: getCapacityStatus(p.annual_capacity, p.requested_capacity),
    currently_imported: formatYesNo(p.currently_imported),
    import_substitution: formatYesNo(p.import_substitution),
    homologation_status: p.homologation_status,
    export_status: p.export_status,
  }));

  function resetForm() {
    setEditId(null);
    setForm(emptyForm);
    setExistingImages([]);

    setNewImages((prev) => {
      prev.forEach((img) => URL.revokeObjectURL(img.preview));
      return [];
    });

    setActiveImageIndex(0);
    setDrawerOpen(false);
  }

  function openCreateDrawer() {
    resetForm();
    setDrawerOpen(true);
  }

  function openEditDrawer(product: Product) {
    resetForm();

    setEditId(product.id_product);

    setForm({
      product_name: product.product_name || "",
      category: product.category || "",
      material: product.material || "",
      annual_capacity:
        product.annual_capacity !== null && product.annual_capacity !== undefined
          ? String(product.annual_capacity)
          : "",
      unit: product.unit || "pcs/year",
      constructor_name: product.constructor_name || "",
      requested_capacity:
        product.requested_capacity !== null && product.requested_capacity !== undefined
          ? String(product.requested_capacity)
          : "",
      currently_imported: String(product.currently_imported || 0),
      import_substitution: String(product.import_substitution || 0),
      homologation_status: product.homologation_status || "",
      export_status: product.export_status || "",
    });

    setExistingImages(normalizeImages(product.product_images));
    setDrawerOpen(true);
  }

  function handleInput(name: string, value: string) {
    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  }

  function openFilePicker() {
    fileInputRef.current?.click();
  }

  function handleFiles(e: ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files || []);

    if (!files.length) return;

    const remainingSlots = Math.max(0, 10 - existingImages.length - newImages.length);

    const images = files
      .filter((file) => file.type.startsWith("image/"))
      .slice(0, remainingSlots)
      .map((file) => ({
        file,
        preview: URL.createObjectURL(file),
      }));

    if (!images.length) {
      toast.warning("Sélectionnez uniquement des images");
      return;
    }

    setNewImages((prev) => [...prev, ...images]);
    setActiveImageIndex(existingImages.length + newImages.length);

    e.target.value = "";
  }

  function removeImage(index: number) {
    const oldCount = existingImages.length;

    if (index < oldCount) {
      setExistingImages((prev) => prev.filter((_, i) => i !== index));
    } else {
      const newIndex = index - oldCount;

      setNewImages((prev) => {
        const item = prev[newIndex];

        if (item) {
          URL.revokeObjectURL(item.preview);
        }

        return prev.filter((_, i) => i !== newIndex);
      });
    }

    setActiveImageIndex((prev) => Math.max(0, prev - 1));
  }

  function nextImage() {
    if (!allDrawerImages.length) return;

    setActiveImageIndex((prev) => (prev + 1) % allDrawerImages.length);
  }

  function previousImage() {
    if (!allDrawerImages.length) return;

    setActiveImageIndex((prev) => (prev - 1 + allDrawerImages.length) % allDrawerImages.length);
  }

  function buildFormData() {
    const data = new FormData();

    data.append("id_user", id_user);

    Object.entries(form).forEach(([key, value]) => {
      data.append(key, value);
    });

    data.set("annual_capacity", String(Number(form.annual_capacity) || 0));
    data.set(
      "requested_capacity",
      form.requested_capacity ? String(Number(form.requested_capacity)) : ""
    );
    data.set("currently_imported", String(Number(form.currently_imported) || 0));
    data.set("import_substitution", String(Number(form.import_substitution) || 0));

    data.append("existing_images", JSON.stringify(existingImages));

    newImages.forEach((img) => {
      data.append("photos", img.file);
    });

    return data;
  }

  async function saveProduct() {
    if (!form.product_name.trim()) {
      toast.warning("Nom du produit obligatoire");
      return;
    }

    setSaving(true);

    try {
      const url = editId
        ? `${backendUrl}/api/supplier-products/update/${editId}`
        : `${backendUrl}/api/supplier-products/create`;

      const method = editId ? "PUT" : "POST";

      const res = await fetch(url, {
        method,
        body: buildFormData(),
      });

      if (!res.ok) throw new Error("Erreur serveur");

      toast.success(editId ? t("product_updated") : t("product_added"));

      resetForm();
      loadProducts();
    } catch (error) {
      toast.error(editId ? t("error_updating_product") : t("error_adding_product"));
    } finally {
      setSaving(false);
    }
  }

  function toggleSelectProduct(id: number) {
    setSelectedIds((prev) => {
      const next = new Set(prev);

      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }

      return next;
    });
  }

  function toggleSelectCurrentPage() {
    setSelectedIds((prev) => {
      const next = new Set(prev);

      if (allCurrentSelected) {
        currentIds.forEach((id) => next.delete(id));
      } else {
        currentIds.forEach((id) => next.add(id));
      }

      return next;
    });
  }

  async function deleteSelectedProducts() {
    const ids = Array.from(selectedIds);

    if (!ids.length) return;

    if (!window.confirm(`Supprimer ${ids.length} produit(s) sélectionné(s) ?`)) {
      return;
    }

    try {
      await Promise.all(
        ids.map((id) =>
          fetch(`${backendUrl}/api/supplier-products/${id}`, {
            method: "DELETE",
          })
        )
      );

      toast.success("Sélection supprimée");
      setSelectedIds(new Set());
      loadProducts();
    } catch {
      toast.error("Erreur pendant la suppression de la sélection");
    }
  }

  async function deleteProduct(id: number) {
    if (!window.confirm("Supprimer ce produit ?")) return;

    try {
      await fetch(`${backendUrl}/api/supplier-products/${id}`, {
        method: "DELETE",
      });

      toast.success(t("product_deleted"));

      setSelectedIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });

      loadProducts();
    } catch {
      toast.error("Erreur pendant la suppression");
    }
  }

  function exportExcel() {
    const ws = XLSX.utils.json_to_sheet(selectedData);
    const wb = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(wb, ws, "Products");
    XLSX.writeFile(wb, "products.xlsx");
  }

  function exportCSV() {
    const ws = XLSX.utils.json_to_sheet(selectedData);
    const csv = XLSX.utils.sheet_to_csv(ws);
    const blob = new Blob([csv], {
      type: "text/csv;charset=utf-8;",
    });

    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");

    a.href = url;
    a.download = "products.csv";
    a.click();

    window.URL.revokeObjectURL(url);
  }

  function exportPDF() {
    const doc = new jsPDF();

    autoTable(doc, {
      head: [
        [
          "Produit",
          "Catégorie",
          "Matériau",
          "Capacité",
          "Besoin",
          "Écart",
          "Statut",
          "Homologation",
          "Export",
        ],
      ],
      body: selectedData.map((p) => [
        p.product_name,
        p.category,
        p.material,
        p.annual_capacity,
        p.requested_capacity || "",
        p.capacity_gap ?? "",
        p.capacity_status,
        p.homologation_status || "",
        p.export_status || "",
      ]),
    });

    doc.save("products.pdf");
  }

  const activeImage = allDrawerImages[activeImageIndex];

  return (
    <div className="container-fluid supplier-products-page">
      <style>{`
        .supplier-products-page {
          padding: 18px;
        }

        .sp-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          margin-bottom: 18px;
        }

        .sp-title {
          display: flex;
          align-items: center;
          gap: 10px;
          font-weight: 700;
          color: #111827;
          margin: 0;
        }

        .sp-card {
          background: #fff;
          border: 1px solid #e5e7eb;
          border-radius: 14px;
          box-shadow: 0 8px 28px rgba(15, 23, 42, .06);
          overflow: hidden;
        }

        .sp-toolbar {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          padding: 14px;
          border-bottom: 1px solid #eef2f7;
        }

        .sp-toolbar-actions {
          display: flex;
          align-items: center;
          gap: 10px;
          flex-wrap: wrap;
          justify-content: flex-end;
        }

        .sp-selection-badge {
          display: flex;
          align-items: center;
          gap: 8px;
          white-space: nowrap;
        }

        .sp-check-cell {
          width: 46px;
          text-align: center;
          vertical-align: middle;
        }

        .sp-search {
          max-width: 460px;
        }

        .sp-table-wrap {
          overflow: auto;
          min-height: 360px;
        }

        .sp-table {
          margin-bottom: 0;
          white-space: nowrap;
        }

        .sp-table thead th {
          position: sticky;
          top: 0;
          background: #f8fafc;
          color: #0f172a;
          font-size: 13px;
          border-bottom: 1px solid #e5e7eb;
          z-index: 1;
        }

        .sp-table tbody td {
          vertical-align: middle;
          font-size: 13px;
        }

        .sp-thumb {
          width: 54px;
          height: 54px;
          border-radius: 10px;
          object-fit: cover;
          border: 1px solid #e5e7eb;
          background: #f8fafc;
          cursor: pointer;
          transition: transform .15s ease, box-shadow .15s ease;
        }

        .sp-thumb:hover {
          transform: scale(1.04);
          box-shadow: 0 8px 18px rgba(15, 23, 42, .14);
        }

        .sp-thumb-empty {
          width: 54px;
          height: 54px;
          border-radius: 10px;
          border: 1px dashed #cbd5e1;
          background: #f8fafc;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #94a3b8;
          cursor: pointer;
        }

        .offcanvas.sp-drawer {
          width: min(880px, calc(100vw - 110px)) !important;
          max-width: calc(100vw - 110px);
          border-left: 1px solid #e5e7eb;
          box-shadow: -18px 0 45px rgba(15, 23, 42, .18);
        }

        .sp-drawer-header {
          border-bottom: 1px solid #e5e7eb;
          padding: 18px 22px;
          background: #fff;
        }

        .sp-drawer-body {
          background: #f8fafc;
          padding: 18px;
          overflow-x: hidden;
          overflow-y: auto;
        }

        .sp-editor {
          display: grid;
          grid-template-columns: 300px minmax(0, 1fr);
          gap: 18px;
          align-items: start;
          width: 100%;
          max-width: 100%;
        }

        .sp-photo-panel,
        .sp-form-panel {
          background: #fff;
          border: 1px solid #e5e7eb;
          border-radius: 16px;
          padding: 14px;
          min-width: 0;
          box-shadow: 0 10px 24px rgba(15, 23, 42, .05);
        }

        .sp-form-panel .row {
          --bs-gutter-x: 12px;
        }

        .sp-form-panel .form-label {
          font-size: 13px;
          font-weight: 600;
          color: #334155;
          margin-bottom: 6px;
        }

        .sp-form-panel .form-control,
        .sp-form-panel .form-select {
          min-height: 42px;
          border-radius: 10px;
        }

        .sp-dropzone {
          height: 250px;
          border: 2px dashed #f59e0b;
          border-radius: 16px;
          background: #fffbeb;
          display: flex;
          align-items: center;
          justify-content: center;
          text-align: center;
          cursor: pointer;
          overflow: hidden;
          position: relative;
        }

        .sp-dropzone:hover {
          background: #fff7ed;
        }

        .sp-preview-img {
          width: 100%;
          height: 100%;
          object-fit: contain;
          background: #fff;
        }

        .sp-empty-photo {
          color: #92400e;
          padding: 18px;
        }

        .sp-empty-photo svg {
          font-size: 38px;
          color: #f59e0b;
          margin-bottom: 10px;
        }

        .sp-photo-actions {
          position: absolute;
          inset: auto 10px 10px 10px;
          display: flex;
          justify-content: space-between;
          pointer-events: none;
        }

        .sp-photo-actions button {
          pointer-events: auto;
          border: 0;
          width: 36px;
          height: 36px;
          border-radius: 999px;
          background: rgba(15, 23, 42, .78);
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .sp-remove-active {
          position: absolute;
          top: 10px;
          right: 10px;
          border: 0;
          width: 34px;
          height: 34px;
          border-radius: 999px;
          background: rgba(220, 38, 38, .9);
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .sp-miniatures {
          display: flex;
          gap: 8px;
          overflow: auto;
          margin-top: 12px;
          padding-bottom: 4px;
        }

        .sp-miniature {
          width: 62px;
          height: 62px;
          border-radius: 12px;
          object-fit: cover;
          border: 2px solid transparent;
          cursor: pointer;
          background: #fff;
        }

        .sp-miniature.active {
          border-color: #f59e0b;
        }

        .sp-upload-btn {
          width: 100%;
          margin-top: 12px;
        }

        .sp-footer {
          display: flex;
          justify-content: flex-end;
          gap: 10px;
          padding: 14px 22px;
          border-top: 1px solid #e5e7eb;
          background: #fff;
          position: sticky;
          bottom: 0;
          z-index: 3;
        }

        @media (max-width: 760px) {
          .offcanvas.sp-drawer {
            width: 100vw !important;
            max-width: 100vw;
          }

          .sp-editor {
            grid-template-columns: 1fr;
          }

          .sp-toolbar,
          .sp-header {
            flex-direction: column;
            align-items: stretch;
          }
        }
      `}</style>

      <div className="sp-header">
        <h4 className="sp-title">
          <FaBox /> {t("products")} <Badge bg="secondary">{products.length}</Badge>
        </h4>

        <div className="d-flex gap-2 justify-content-end">
          <Dropdown>
            <Dropdown.Toggle variant="outline-secondary">
              <FaDownload className="me-1" /> {t("export")}
            </Dropdown.Toggle>

            <Dropdown.Menu>
              <Dropdown.Item onClick={exportExcel}>Excel</Dropdown.Item>
              <Dropdown.Item onClick={exportCSV}>CSV</Dropdown.Item>
              <Dropdown.Item onClick={exportPDF}>PDF</Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>

          <Button variant="warning" onClick={openCreateDrawer}>
            <FaPlus className="me-1" /> {t("add_product")}
          </Button>
        </div>
      </div>

      <div className="sp-card">
        <div className="sp-toolbar">
          <div className="input-group sp-search">
            <Dropdown>
              <Dropdown.Toggle variant="light">{searchField}</Dropdown.Toggle>

              <Dropdown.Menu>
                <Dropdown.Item onClick={() => setSearchField("product_name")}>
                  {t("product")}
                </Dropdown.Item>
                <Dropdown.Item onClick={() => setSearchField("category")}>
                  {t("category")}
                </Dropdown.Item>
                <Dropdown.Item onClick={() => setSearchField("material")}>
                  {t("material")}
                </Dropdown.Item>
                <Dropdown.Item onClick={() => setSearchField("constructor_name")}>
                  Constructeur
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>

            <Form.Control
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(0);
              }}
              placeholder="Rechercher ici..."
            />
          </div>

          <div className="sp-toolbar-actions">
            {selectedIds.size > 0 && (
              <>
                <span className="sp-selection-badge badge bg-warning text-dark">
                  {selectedIds.size} sélectionné(s)
                </span>

                <Button size="sm" variant="outline-danger" onClick={deleteSelectedProducts}>
                  Supprimer la sélection
                </Button>
              </>
            )}

            <Form.Select
              style={{ width: 110 }}
              value={limit}
              onChange={(e) => {
                setLimit(Number(e.target.value));
                setPage(0);
              }}
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
            </Form.Select>
          </div>
        </div>

        {loading ? (
          <div className="text-center p-5">
            <PropagateLoader />
          </div>
        ) : (
          <div className="sp-table-wrap">
            <Table hover responsive className="sp-table">
              <thead>
                <tr>
                  <th className="sp-check-cell">
                    <Form.Check
                      type="checkbox"
                      checked={allCurrentSelected}
                      disabled={!current.length}
                      onChange={toggleSelectCurrentPage}
                      aria-label="Sélectionner les produits affichés"
                    />
                  </th>
                  <th>Photo</th>
                  <th>{t("product")}</th>
                  <th>{t("category")}</th>
                  <th>{t("material")}</th>
                  <th>{t("annual_capacity")}</th>
                  <th>{t("unit")}</th>
                  <th>Constructeur</th>
                  <th>Besoin Constructeur</th>
                  <th>Écart Capacité</th>
                  <th>Statut Capacitaire</th>
                  <th>Importé</th>
                  <th>Substitution</th>
                  <th>Homologation</th>
                  <th>Export</th>
                  <th className="text-center">Actions</th>
                </tr>
              </thead>

              <tbody>
                {current.length ? (
                  current.map((p) => {
                    const imgs = normalizeImages(p.product_images);
                    const firstImage = imgs[0] || "";
                    const imageUrl = getImageUrl(firstImage);
                    const gap = getCapacityGap(p.annual_capacity, p.requested_capacity);
                    const status = getCapacityStatus(p.annual_capacity, p.requested_capacity);

                    return (
                      <tr key={p.id_product}>
                        <td className="sp-check-cell">
                          <Form.Check
                            type="checkbox"
                            checked={selectedIds.has(p.id_product)}
                            onChange={() => toggleSelectProduct(p.id_product)}
                            aria-label={`Sélectionner ${p.product_name}`}
                          />
                        </td>

                        <td>
                          {imageUrl ? (
                            <img
                              src={imageUrl}
                              alt={p.product_name}
                              title={p.product_name}
                              className="sp-thumb"
                              onClick={() => openEditDrawer(p)}
                              onError={(e) => {
                                e.currentTarget.style.display = "none";

                                const parent = e.currentTarget.parentElement;

                                if (parent && !parent.querySelector(".sp-thumb-empty")) {
                                  const fallback = document.createElement("div");
                                  fallback.className = "sp-thumb-empty";
                                  fallback.innerHTML = "🖼️";
                                  fallback.onclick = () => openEditDrawer(p);
                                  parent.prepend(fallback);
                                }
                              }}
                            />
                          ) : (
                            <div className="sp-thumb-empty" onClick={() => openEditDrawer(p)}>
                              <FaImage />
                            </div>
                          )}

                          {imgs.length > 1 && (
                            <Badge bg="dark" className="ms-1">
                              +{imgs.length - 1}
                            </Badge>
                          )}
                        </td>

                        <td>{displayValue(p.product_name)}</td>
                        <td>{displayValue(p.category)}</td>
                        <td>{displayValue(p.material)}</td>
                        <td>{displayValue(p.annual_capacity)}</td>
                        <td>{displayValue(p.unit)}</td>
                        <td>{displayValue(p.constructor_name)}</td>
                        <td>{displayValue(p.requested_capacity)}</td>
                        <td>{gap ?? "Non renseigné"}</td>

                        <td>
                          <span className={getCapacityBadgeClass(status)}>{status}</span>
                        </td>

                        <td>{formatYesNo(p.currently_imported)}</td>
                        <td>{formatYesNo(p.import_substitution)}</td>
                        <td>{displayValue(p.homologation_status)}</td>
                        <td>{displayValue(p.export_status)}</td>

                        <td className="text-center">
                          <Button
                            size="sm"
                            variant="outline-warning"
                            className="me-2"
                            onClick={() => openEditDrawer(p)}
                          >
                            <FaEdit />
                          </Button>

                          <Button
                            size="sm"
                            variant="outline-danger"
                            onClick={() => deleteProduct(p.id_product)}
                          >
                            <FaTrash />
                          </Button>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={16} className="text-center py-5">
                      {t("no_products")}
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        )}

        <div className="d-flex justify-content-between align-items-center p-3 border-top">
          <span>
            {t("displaying")} {current.length} {t("out_of")} {filtered.length}
            {selectedIds.size > 0 ? ` — ${selectedIds.size} sélectionné(s)` : ""}
          </span>

          <ReactPaginate
            previousLabel={t("previous")}
            nextLabel={t("next")}
            breakLabel="..."
            pageCount={pageCount}
            marginPagesDisplayed={2}
            pageRangeDisplayed={3}
            onPageChange={(d) => setPage(d.selected)}
            containerClassName="pagination justify-content-end mb-0"
            pageClassName="page-item"
            pageLinkClassName="page-link"
            previousClassName="page-item"
            previousLinkClassName="page-link"
            nextClassName="page-item"
            nextLinkClassName="page-link"
            activeClassName="active"
            forcePage={Math.min(page, pageCount - 1)}
          />
        </div>
      </div>

      <Offcanvas show={drawerOpen} onHide={resetForm} placement="end" className="sp-drawer">
        <Offcanvas.Header closeButton className="sp-drawer-header">
          <Offcanvas.Title>{editId ? "Modifier produit" : "Ajouter produit"}</Offcanvas.Title>
        </Offcanvas.Header>

        <Offcanvas.Body className="sp-drawer-body">
          <div className="sp-editor">
            <div className="sp-photo-panel">
              <strong>Photos du produit</strong>

              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                hidden
                onChange={handleFiles}
              />

              <div className="sp-dropzone mt-3" onClick={openFilePicker}>
                {activeImage ? (
                  <>
                    <img
                      src={activeImage.preview}
                      alt={form.product_name || "Produit"}
                      title={form.product_name || "Produit"}
                      className="sp-preview-img"
                      onError={(e) => {
                        e.currentTarget.style.display = "none";
                      }}
                    />

                    <button
                      type="button"
                      className="sp-remove-active"
                      onClick={(e) => {
                        e.stopPropagation();
                        removeImage(activeImageIndex);
                      }}
                    >
                      <FaTimes />
                    </button>

                    {allDrawerImages.length > 1 && (
                      <div className="sp-photo-actions">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            previousImage();
                          }}
                        >
                          <FaChevronLeft />
                        </button>

                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            nextImage();
                          }}
                        >
                          <FaChevronRight />
                        </button>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="sp-empty-photo">
                    <FaUpload />
                    <div className="fw-bold">Cliquer ici pour ajouter des photos</div>
                    <small>Une ou plusieurs images</small>
                  </div>
                )}
              </div>

              <Button variant="outline-warning" className="sp-upload-btn" onClick={openFilePicker}>
                <FaImage className="me-2" />
                Ajouter une ou plusieurs photos
              </Button>

              {allDrawerImages.length > 0 && (
                <div className="sp-miniatures">
                  {allDrawerImages.map((img, index) => (
                    <img
                      key={`${img.path}-${index}`}
                      src={img.preview}
                      alt={form.product_name || "Miniature produit"}
                      title={form.product_name || "Miniature produit"}
                      className={`sp-miniature ${index === activeImageIndex ? "active" : ""}`}
                      onClick={() => setActiveImageIndex(index)}
                      onError={(e) => {
                        e.currentTarget.style.opacity = "0.35";
                      }}
                    />
                  ))}
                </div>
              )}

              <div className="text-muted small mt-2">
                Anciennes photos gardées : {existingImages.length} | nouvelles photos :{" "}
                {newImages.length}
              </div>
            </div>

            <div className="sp-form-panel">
              <Form>
                <div className="row">
                  <Form.Group className="mb-3 col-md-12">
                    <Form.Label>Nom du produit</Form.Label>
                    <Form.Control
                      value={form.product_name}
                      onChange={(e) => handleInput("product_name", e.target.value)}
                    />
                  </Form.Group>

                  <Form.Group className="mb-3 col-md-6">
                    <Form.Label>Catégorie</Form.Label>
                    <Form.Control
                      value={form.category}
                      onChange={(e) => handleInput("category", e.target.value)}
                    />
                  </Form.Group>

                  <Form.Group className="mb-3 col-md-6">
                    <Form.Label>Matériau</Form.Label>
                    <Form.Control
                      value={form.material}
                      onChange={(e) => handleInput("material", e.target.value)}
                    />
                  </Form.Group>

                  <Form.Group className="mb-3 col-md-6">
                    <Form.Label>Capacité annuelle</Form.Label>
                    <Form.Control
                      type="number"
                      value={form.annual_capacity}
                      onChange={(e) => handleInput("annual_capacity", e.target.value)}
                    />
                  </Form.Group>

                  <Form.Group className="mb-3 col-md-6">
                    <Form.Label>Unité</Form.Label>
                    <Form.Control
                      value={form.unit}
                      onChange={(e) => handleInput("unit", e.target.value)}
                    />
                  </Form.Group>

                  <Form.Group className="mb-3 col-md-12">
                    <Form.Label>Constructeur concerné</Form.Label>
                    <Form.Control
                      value={form.constructor_name}
                      onChange={(e) => handleInput("constructor_name", e.target.value)}
                    />
                  </Form.Group>

                  <Form.Group className="mb-3 col-md-12">
                    <Form.Label>Capacité demandée constructeur</Form.Label>
                    <Form.Control
                      type="number"
                      value={form.requested_capacity}
                      onChange={(e) => handleInput("requested_capacity", e.target.value)}
                    />
                  </Form.Group>

                  <Form.Group className="mb-3 col-md-6">
                    <Form.Label>Produit importé actuellement</Form.Label>
                    <Form.Select
                      value={form.currently_imported}
                      onChange={(e) => handleInput("currently_imported", e.target.value)}
                    >
                      <option value="0">Non</option>
                      <option value="1">Oui</option>
                    </Form.Select>
                  </Form.Group>

                  <Form.Group className="mb-3 col-md-6">
                    <Form.Label>Substitution importation</Form.Label>
                    <Form.Select
                      value={form.import_substitution}
                      onChange={(e) => handleInput("import_substitution", e.target.value)}
                    >
                      <option value="0">Non</option>
                      <option value="1">Oui</option>
                    </Form.Select>
                  </Form.Group>

                  <Form.Group className="mb-3 col-md-6">
                    <Form.Label>Homologation</Form.Label>
                    <Form.Select
                      value={form.homologation_status}
                      onChange={(e) => handleInput("homologation_status", e.target.value)}
                    >
                      <option value="">Non renseigné</option>
                      <option value="Validée">Validée</option>
                      <option value="En cours">En cours</option>
                      <option value="Requise">Requise</option>
                      <option value="Refusée">Refusée</option>
                    </Form.Select>
                  </Form.Group>

                  <Form.Group className="mb-3 col-md-6">
                    <Form.Label>Export</Form.Label>
                    <Form.Select
                      value={form.export_status}
                      onChange={(e) => handleInput("export_status", e.target.value)}
                    >
                      <option value="">Non renseigné</option>
                      <option value="Export-ready">Export-ready</option>
                      <option value="Potentiel">Potentiel</option>
                      <option value="Non exportable">Non exportable</option>
                    </Form.Select>
                  </Form.Group>
                </div>
              </Form>
            </div>
          </div>
        </Offcanvas.Body>

        <div className="sp-footer">
          <Button variant="secondary" onClick={resetForm} disabled={saving}>
            {t("cancel")}
          </Button>

          <Button variant="warning" onClick={saveProduct} disabled={saving}>
            {saving ? "Enregistrement..." : t("save")}
          </Button>
        </div>
      </Offcanvas>
    </div>
  );
}